import { useEffect, useState } from "react";
import { supabase } from "./supabase";

// Hand-typed mirror of supabase/migrations/20260527110000_seo_schema.sql.
// Replaced by generated `Database` types after the next `supabase gen types`.

export type SeoCrawlRun = {
  id: string;
  target_host: string;
  started_at: string;
  finished_at: string | null;
  status: "running" | "ok" | "error";
  pages_discovered: number;
  pages_succeeded: number;
  pages_failed: number;
  error: string | null;
  raw: unknown;
  // Synthesised to satisfy SyncStatus structural type:
  campaigns_upserted: null;
  metrics_upserted: null;
};

export type SeoIssue = {
  type: string;
  severity: "info" | "warning" | "error";
  message: string;
};

export type SeoPage = {
  url: string;
  host: string;
  path: string;
  last_crawl_id: string | null;
  status_code: number | null;
  content_type: string | null;
  redirected_from: string | null;
  title: string | null;
  meta_description: string | null;
  h1: string | null;
  canonical: string | null;
  robots_meta: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  word_count: number | null;
  internal_link_count: number | null;
  external_link_count: number | null;
  structured_data: unknown;
  issues: SeoIssue[];
  html_hash: string | null;
  response_time_ms: number | null;
  fetched_at: string;
};

export type SeoData = {
  pages: SeoPage[];
  runs: SeoCrawlRun[];
  loading: boolean;
  error: string | null;
};

export function useSeoData(): SeoData {
  const [pages, setPages] = useState<SeoPage[]>([]);
  const [runs, setRuns] = useState<SeoCrawlRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const client = supabase();
    let cancelled = false;

    async function fetchAll() {
      try {
        const [p, r] = await Promise.all([
          client.from("seo_pages").select("*"),
          client.from("seo_crawl_runs").select("*").order("started_at", { ascending: false }).limit(20),
        ]);

        if (cancelled) return;
        if (p.error) throw p.error;
        if (r.error) throw r.error;

        setPages((p.data as SeoPage[]) ?? []);
        setRuns(
          ((r.data ?? []) as Omit<SeoCrawlRun, "campaigns_upserted" | "metrics_upserted">[])
            .map((row) => ({
              ...row,
              campaigns_upserted: null,
              metrics_upserted: null,
            })),
        );
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();

    const channel = client
      .channel("seo-data")
      .on("postgres_changes", { event: "*", schema: "public", table: "seo_pages" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "seo_crawl_runs" }, fetchAll)
      .subscribe();

    return () => {
      cancelled = true;
      client.removeChannel(channel);
    };
  }, []);

  return { pages, runs, loading, error };
}

export type SeoSummary = {
  total: number;
  errors: number;
  redirects: number;
  withIssues: number;
  avgResponseMs: number;
  totalIssues: number;
  issuesBySeverity: { error: number; warning: number; info: number };
};

export function summarisePages(pages: SeoPage[]): SeoSummary {
  let errors = 0;
  let redirects = 0;
  let withIssues = 0;
  let totalResponse = 0;
  let responseCount = 0;
  let totalIssues = 0;
  const issuesBySeverity = { error: 0, warning: 0, info: 0 };

  for (const p of pages) {
    if (p.status_code != null) {
      if (p.status_code >= 400) errors += 1;
      else if (p.status_code >= 300) redirects += 1;
    }
    if (p.issues?.length > 0) {
      withIssues += 1;
      totalIssues += p.issues.length;
      for (const issue of p.issues) {
        const sev = issue.severity;
        if (sev === "error" || sev === "warning" || sev === "info") {
          issuesBySeverity[sev] += 1;
        }
      }
    }
    if (p.response_time_ms != null) {
      totalResponse += p.response_time_ms;
      responseCount += 1;
    }
  }

  return {
    total: pages.length,
    errors,
    redirects,
    withIssues,
    avgResponseMs: responseCount > 0 ? Math.round(totalResponse / responseCount) : 0,
    totalIssues,
    issuesBySeverity,
  };
}
